import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { ECTopbar } from './ECTopbar';
import FolderIcon from '@mui/icons-material/Folder';
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import moment from 'moment';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import { updateOrCreate } from '../special/updateOrCreate';
import Loader from '../special/Loader';
import CreateIcon from '@mui/icons-material/Create';
import { ECSenderPreview } from './ECSenderPreview';
import CategoryIcon from '@mui/icons-material/Category';
import EmailIcon from '@mui/icons-material/Email';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ExoInfiniteScroll from '../exo/ExoInfiniteScroll';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

export const ECEmailsList = forwardRef(
  (
    {
      inbox,
      folderMap,
      onSelect,
      selectedEmail,
      onFolderChange,
      selectedFolder,
      onNewMail,
      noKeyAction,
      onEmailChange,
      credentials,
      onFolderSelect,
      onChangeView,
      isMobile,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const config = useConfig();
    const { t } = useTranslation();

    const scrollContainerRef = useRef(null);

    const { name, unseenCount, id } = inbox || {};

    const Icon = folderMap[name]?.Icon || FolderIcon;
    const label = folderMap[name]?.label || name;
    const color = folderMap[name]?.color || 'unset';

    const [apiData, setApiData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      loadEmails(id);
    }, [id, config]);

    useEffect(() => {
      if (!credentials || !selectedFolder) return;
      //check for new mails in folder
      var unseenCountDiff = false;
      var mailCountDiff = false;
      // find the right folder and compare with selected folder
      credentials.data.forEach((cred) => {
        const folders = cred.inbox.folders;
        folders.forEach((folder) => {
          if (folder.id === selectedFolder.id) {
            unseenCountDiff = folder.unseenCount !== selectedFolder.unseenCount;
            mailCountDiff = folder.mailCount !== selectedFolder.mailCount;
            onFolderSelect(folder);
          }
        });
      });

      // reload list if there is something new in the selected folder
      if (mailCountDiff) loadEmails(id, true);
    }, [credentials]);

    function loadEmails(inboxId, shadow) {
      if (!shadow) setApiData(null);
      DBRequest({
        config,
        path: `imap-emails?folderId[eq]=${inboxId}`,
        onResponse: setApiData,
      });
    }

    function handleDeleteEmail() {
      if (selection.length > 0)
        DBRequest({
          config,
          path: `imap-emails/bulk-delete`,
          method: API_POST({ emailIds: selection }),
          onLoading: setIsLoading,
          onResponse: () => {
            const newEmails = apiData.data.filter(
              (obj) => !selection.includes(obj.id)
            );
            setApiData({ ...apiData, data: newEmails });
            onSelect(null);
            setSelection([]);
            calculateFolderUpdate(newEmails);
          },
        });
    }

    useEffect(() => {
      const handleKeyDown = (e) => {
        if (!noKeyAction && e.key === 'Delete') {
          handleDeleteEmail();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [apiData, noKeyAction, selectedEmail]);

    function handleEmailChange(res) {
      onEmailChange(res);
      const newEmails = updateOrCreate(apiData, res.data);
      setApiData(newEmails);
      calculateFolderUpdate(newEmails.data);
    }

    function calculateFolderUpdate(emails) {
      // calc unseen email count
      var unseenCount = 0;
      emails.forEach((email) => {
        if (!email.isSeen) unseenCount++;
      });
      onFolderChange({ ...selectedFolder, unseenCount });
    }

    function updateEmail(email) {
      setApiData(updateOrCreate(apiData, email));
    }

    // exposed functions
    useImperativeHandle(ref, () => ({
      updateEmail,
    }));

    // selection stuff
    const [selection, setSelection] = useState([]);
    const [lastSelectedId, setLastSelectedId] = useState(null);

    const handleSelectionChange = (element, event) => {
      onSelect(element);
      const { id } = element;
      const isSelected = selection.includes(id);

      if (event.shiftKey && lastSelectedId !== null) {
        // SHIFT: Select range
        const startIndex = apiData.data.findIndex(
          (item) => item.id === lastSelectedId
        );
        const endIndex = apiData.data.findIndex((item) => item.id === id);

        if (startIndex !== -1 && endIndex !== -1) {
          const [from, to] =
            startIndex < endIndex
              ? [startIndex, endIndex]
              : [endIndex, startIndex];
          const range = apiData.data.slice(from, to + 1).map((item) => item.id);
          const newSelection = Array.from(new Set([...selection, ...range])); // prevent duplicates
          setSelection(newSelection);
        }
      } else if (event.metaKey || event.ctrlKey) {
        // CTRL/CMD: Toggle selection
        if (isSelected) {
          setSelection(selection.filter((selId) => selId !== id));
        } else {
          setSelection([...selection, id]);
        }
        setLastSelectedId(id);
      } else {
        // Simple click: single selection
        setSelection([id]);
        setLastSelectedId(id);
      }
    };
    return (
      <Box className="h-full flex flex-col max-w-100% w-full overflow-hidden relative max-w-[650px]">
        <ECTopbar
          label={label}
          sublabel={
            unseenCount > 0
              ? unseenCount == 1
                ? `${unseenCount} ${t('message')}`
                : `${unseenCount} ${t('messages')}`
              : ''
          }
        >
          {!isMobile && credentials && credentials.data.length > 0 && (
            <Box className="h-full flex items-center">
              <Tooltip title={t('New Email')}>
                <IconButton onClick={onNewMail}>
                  <CreateIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </ECTopbar>
        {isMobile && (
          <>
            <IconButton
              onClick={() => onChangeView('folder')}
              sx={{
                position: 'absolute',
                bgcolor: colors.bgInput,
                height: '50px',
                width: '50px',
                borderRadius: '15px',
              }}
              className="bottom-2 left-2 z-10 backdrop-blur-md"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: '30px' }} />
            </IconButton>
            <IconButton
              onClick={onNewMail}
              sx={{
                position: 'absolute',
                bgcolor: colors.info,
                height: '50px',
                width: '50px',
              }}
              className="bottom-2 right-2 z-10"
            >
              <CreateIcon sx={{ fontSize: '30px' }} />
            </IconButton>
          </>
        )}
        <Box
          className="flex flex-col h-full overflow-y-auto relative w-full overflow-x-hidden"
          ref={scrollContainerRef}
        >
          <ExoInfiniteScroll
            apiData={apiData}
            setApiData={setApiData}
            scrollContainerRef={scrollContainerRef}
          >
            <Loader active={isLoading} />
            {apiData ? (
              apiData?.data.length > 0 ? (
                apiData?.data.map((email) => (
                  <Fragment key={email.id}>
                    <EmailCard
                      email={email}
                      onSelect={handleSelectionChange}
                      selected={selection.includes(email.id)}
                      onChange={handleEmailChange}
                    />
                    <Divider className="w-[90%] opacity-40" />
                  </Fragment>
                ))
              ) : (
                <Box className="w-full flex items-center justify-center pt-[20px]">
                  <Typography className=" opacity-50">
                    {t('No messages found')}
                  </Typography>
                </Box>
              )
            ) : (
              <ExoSkeleton n={9} className="py-[2px] w-full" />
            )}
          </ExoInfiniteScroll>
        </Box>
      </Box>
    );
  }
);

export const EmailCard = ({ email, onSelect, selected, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const {
    id,
    subject,
    sender,
    senderType,
    date,
    isSeen,
    isFlagged,
    salesObjectId,
    attachmentsCount,
    previousEmailsCount,
  } = email;

  function getDateLabel(dateTime) {
    const dateMoment = moment(dateTime);
    const now = moment();

    if (dateMoment.isSame(now, 'day')) {
      return dateMoment.format('HH:mm');
    } else if (dateMoment.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return `${t('yesterday')} ${dateMoment.format('HH:mm')}`;
    } else {
      return dateMoment.format(config.general.dateOutputFormat);
    }
  }

  function handleClickMail(e) {
    onSelect(email, e);
  }

  function handleFlagClick(e) {
    e.preventDefault();
    e.stopPropagation();
    DBRequest({
      config,
      path: `imap-emails/${id}`,
      method: API_PATCH({ isFlagged: !isFlagged }),
      onResponse: onChange,
    });
  }

  const [dateTime, setDateTime] = useState('');
  useEffect(() => {
    if (config) setDateTime(getDateLabel(date));
  }, [config]);

  return (
    <Button
      sx={{
        justifyContent: 'start',
        borderRadius: '0px',
        bgcolor: selected ? colors.card : 'transparent',
        height: '55px',
        border: `1px solid transparent`,
        '.only-on-hover': {
          transform: isFlagged ? 'translateX(0px)' : 'translateX(50px)',
          transition: 'transform 0.5s ease',
        },
        ':hover': {
          bgcolor: colors.info + '30',
          border: `1px solid ${colors.info}80`,
          '.only-on-hover': {
            transform: 'translateX(0px)',
          },
        },
      }}
      className="flex gap-1 px-2 w-full pl-1 pr-2"
      onClick={handleClickMail}
    >
      <Box
        sx={{
          bgcolor: isFlagged
            ? colors.warning
            : isSeen
            ? 'transparent'
            : colors.info,
        }}
        className="w-[3px] h-full rounded-full"
      />
      <ECSenderPreview sender={sender} type={senderType} subject={subject} />
      <Box className="flex flex-col items-end">
        <Box className="only-on-hover ">
          <IconButton size="small" onClick={handleFlagClick}>
            {isFlagged ? (
              <FlagIcon sx={{ fontSize: '14px', color: colors.warning }} />
            ) : (
              <OutlinedFlagIcon sx={{ fontSize: '14px' }} />
            )}
          </IconButton>
        </Box>
        <Box className="w-full flex gap-2 overflow-hidden items-center">
          {salesObjectId && (
            <Tooltip title={t('linked to sales object')}>
              <Box
                sx={{
                  bgcolor: colors.info + 30,
                  border: `1px solid ${colors.info}`,
                }}
                className="w-[20px] h-[20px] flex items-center justify-center rounded-md"
              >
                <CategoryIcon sx={{ fontSize: '12px' }} />
              </Box>
            </Tooltip>
          )}
          {attachmentsCount > 0 && (
            <Tooltip
              title={`${attachmentsCount} ${
                attachmentsCount === 1 ? t('attachment') : t('attachments')
              }`}
            >
              <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md opacity-50">
                <AttachmentIcon
                  sx={{ fontSize: '12px' }}
                  className="ml-[2px]"
                />
              </Box>
            </Tooltip>
          )}
          {previousEmailsCount > 0 && (
            <Tooltip
              title={`${previousEmailsCount} ${
                previousEmailsCount === 1
                  ? t('message in conversation')
                  : t('messages in conversation')
              }`}
            >
              <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md opacity-50">
                <EmailIcon sx={{ fontSize: '12px' }} className="ml-[2px]" />
              </Box>
            </Tooltip>
          )}
          <Typography
            textAlign="end"
            fontSize="9px"
            className=" whitespace-nowrap opacity-40"
          >
            {dateTime}
          </Typography>
        </Box>
      </Box>
    </Button>
  );
};
